import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle, ArrowRight, ChevronDown, Check,
  Clock, FileText, BarChart2, Users, RefreshCw, Zap,
} from 'lucide-react';

const FEATURES = [
  {
    icon: FileText,
    headline: 'Every service. Every client. One view.',
    copy: 'Track GST, ITR, TDS, ROC, Payroll and 20+ compliance categories for every client — with status, assignee, and due dates visible at a glance.',
  },
  {
    icon: Clock,
    headline: 'Deadlines that manage themselves.',
    copy: 'Internal and regulatory due dates tracked separately. Indian statutory dates — GSTR, TDS filing, ITR, ROC — computed automatically based on entity type.',
  },
  {
    icon: RefreshCw,
    headline: 'Services that renew on their own.',
    copy: 'Mark a monthly service done and the next period is created instantly with the correct due date. No manual entry. No missed cycles.',
  },
  {
    icon: Users,
    headline: 'Your team, structured for scale.',
    copy: 'Admin, Manager, and Consultant roles with granular permissions. Assign clients to team members. Each consultant sees only what they need to.',
  },
  {
    icon: BarChart2,
    headline: 'Never lose a lead.',
    copy: 'A clean pipeline from first enquiry to onboarded client. Track status, notes, and follow-ups for every prospect — all in one place.',
  },
  {
    icon: Zap,
    headline: 'Time tracked. Revenue justified.',
    copy: "Log time per service and per team member. Know exactly where your firm's hours go and make informed staffing decisions.",
  },
];

const STEPS = [
  {
    num: '01',
    title: 'Add your clients and services',
    desc: 'Onboard a client in under 2 minutes. Choose from 20+ compliance categories. Set frequency — monthly, quarterly, or annual.',
  },
  {
    num: '02',
    title: 'Deadlines fill in automatically',
    desc: 'Indian statutory deadlines (GST, TDS, ITR, ROC) are pre-loaded by entity type and period. No manual research. No guesswork.',
  },
  {
    num: '03',
    title: 'Mark done. Next period appears.',
    desc: 'Complete a service and the next cycle is created instantly. Archive happens automatically once payment is confirmed.',
  },
];

const PLANS = [
  {
    name: 'Starter',
    price: '₹999',
    tagline: 'For solo practitioners',
    highlight: false,
    features: [
      'Up to 3 team members',
      'Up to 50 clients',
      'Compliance service tracking',
      'Deadline calendar',
      'Leads pipeline',
      'Email support',
    ],
  },
  {
    name: 'Growth',
    price: '₹2,499',
    tagline: 'For growing CA firms',
    highlight: true,
    badge: 'Most Popular',
    features: [
      'Up to 15 team members',
      'Up to 500 clients',
      'Everything in Starter',
      'Role-based access control',
      'Statutory due date engine',
      'Excel import & export',
      'Priority support',
    ],
  },
  {
    name: 'Scale',
    price: '₹4,999',
    tagline: 'For large multi-partner firms',
    highlight: false,
    features: [
      'Unlimited team members',
      'Unlimited clients',
      'Everything in Growth',
      'Timesheet & team hours',
      'Advanced reporting',
      'US compliance deadlines',
      'Dedicated account manager',
    ],
  },
];

const COMPLIANCE_TAGS = [
  'GSTR-1', 'GSTR-3B', 'TDS Filing', 'TDS Payment', 'Income Tax Return',
  'Advance Tax', 'ROC Filing', 'Payroll', 'Audit', 'FEMA Compliance',
  'Due Diligence', 'Bookkeeping',
];

const FAQS = [
  {
    q: 'Can multiple team members use the same account?',
    a: 'Yes. Each plan supports a set number of team members, each with their own login and role-based access — Admin, Manager, or Consultant.',
  },
  {
    q: 'Can a consultant see all clients?',
    a: 'No. Consultants only see clients explicitly assigned to them by the admin. Admins and Managers retain full visibility across the firm.',
  },
  {
    q: 'How do statutory due dates work?',
    a: 'When you add a service and set the period, DueDate computes the regulatory due date based on Indian law — Income Tax Act, GST, Companies Act, LLP Act — and the client\'s entity type. You can always override it.',
  },
  {
    q: 'What happens when a recurring service is marked done?',
    a: 'The next period is automatically created with the correct due date advanced. The completed service archives itself once marked Post Payment.',
  },
  {
    q: 'Is there a free trial?',
    a: 'Yes — 14-day free trial on any plan. No credit card required to get started.',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── Navbar ─────────────────────────────────────────────────── */}
      <header>
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-100">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-sm">D</span>
              </div>
              <span className="font-bold text-xl tracking-tight">DueDate</span>
            </div>
            <div className="flex items-center gap-6">
              <a href="#how-it-works"
                 className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors hidden md:block">
                How it works
              </a>
              <a href="#pricing"
                 className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors hidden md:block">
                Pricing
              </a>
              <button onClick={() => navigate('/login')}
                      className="text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors hidden sm:block">
                Log in
              </button>
              <button onClick={() => navigate('/login')}
                      className="flex items-center gap-2 bg-gray-900 text-white text-sm font-bold px-5 py-2.5 rounded-lg hover:bg-gray-700 transition-colors">
                Get started <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </nav>
      </header>

      <main>
      {/* ── Hero — dark ─────────────────────────────────────────────── */}
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-6xl mx-auto px-6 pt-24 pb-28 text-center">

          <div className="inline-flex items-center gap-2 bg-white/10 text-gray-300 text-xs font-semibold px-4 py-2 rounded-full mb-10 tracking-wide border border-white/10">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            Purpose-built for Indian CA &amp; Tax Firms
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[1.05] tracking-tight mb-8 max-w-4xl mx-auto">
            The command centre for practices that run on precision.
          </h1>

          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-12 leading-relaxed">
            DueDate brings every client, deadline, service, and team member into one intelligent workspace.
            No spreadsheets. No missed statutory dates. No dropped follow-ups.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/login')}
                    className="flex items-center justify-center gap-2.5 bg-white text-gray-900 font-black px-8 py-4 rounded-xl hover:bg-gray-100 transition-all hover:-translate-y-0.5 shadow-2xl shadow-black/40 text-sm tracking-wide">
              Start your free trial <ArrowRight size={15} />
            </button>
            <a href="#pricing"
               className="flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-xl border border-white/15 hover:bg-white/15 transition-all text-sm">
              View pricing
            </a>
          </div>

          <p className="text-xs text-gray-400 mt-5 tracking-wide">
            14-day free trial &nbsp;·&nbsp; No credit card required &nbsp;·&nbsp; Setup in minutes
          </p>

          {/* Stats strip */}
          <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 rounded-2xl overflow-hidden border border-white/10">
            {[
              { value: '20+',        label: 'Compliance categories' },
              { value: 'Pre-loaded', label: 'Indian statutory deadlines' },
              { value: 'Automatic',  label: 'Service period renewal' },
            ].map(({ value, label }) => (
              <div key={label} className="bg-white/5 px-8 py-7 text-center border-b sm:border-b-0 sm:border-r border-white/10 last:border-0">
                <p className="text-3xl font-black text-white mb-1.5 tracking-tight">{value}</p>
                <p className="text-xs text-gray-400 font-medium tracking-wide">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Compliance tags ─────────────────────────────────────────── */}
      <section className="border-b border-gray-100 py-10 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-center text-xs text-gray-500 font-semibold uppercase tracking-widest mb-6">
            Covers every major compliance area
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {COMPLIANCE_TAGS.map(tag => (
              <span key={tag}
                    className="flex items-center gap-1.5 text-xs font-medium bg-white text-gray-700 border border-gray-200 px-3.5 py-1.5 rounded-full shadow-sm">
                <Check size={10} className="text-gray-400" /> {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">How it works</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 tracking-tight">
              Three steps to a deadline-free firm.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {STEPS.map(({ num, title, desc }) => (
              <div key={num}>
                <span aria-hidden="true"
                      className="block text-8xl font-black text-[#8F8F8F] leading-none select-none -mb-2">
                  {num}
                </span>
                <h3 className="text-lg font-bold text-gray-900 mb-3 mt-1">{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-y border-gray-100 py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Features</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 tracking-tight">
              Built for the way CA firms actually work.
            </h2>
            <p className="text-gray-500 max-w-xl mx-auto mt-4 leading-relaxed">
              Every feature is designed around Indian compliance workflows — not adapted from generic project management tools.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, headline, copy }) => (
              <div key={headline}
                   className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-200 group">
                <div className="w-10 h-10 bg-gray-900 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform duration-200">
                  <Icon size={17} className="text-white" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 leading-snug">{headline}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ─────────────────────────────────────────────────── */}
      <section id="pricing" className="py-28">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Pricing</p>
            <h2 className="text-4xl sm:text-5xl font-black text-gray-900 tracking-tight">
              Simple, transparent pricing.
            </h2>
            <p className="text-gray-500 mt-4">No hidden fees. No lock-in. Cancel anytime.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            {PLANS.map((plan) => (
              <div key={plan.name}
                   className={`relative rounded-2xl p-8 flex flex-col ${
                     plan.highlight
                       ? 'bg-[#0F172A] text-white shadow-2xl shadow-gray-300 md:scale-[1.04] md:z-10'
                       : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all'
                   }`}>

                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-white text-gray-900 text-xs font-black px-4 py-1.5 rounded-full border border-gray-200 shadow-md whitespace-nowrap tracking-wide">
                      ✦ {plan.badge}
                    </span>
                  </div>
                )}

                <div className="mb-8">
                  <p className={`text-xs font-black uppercase tracking-widest mb-4 ${plan.highlight ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plan.name}
                  </p>
                  <div className="flex items-end gap-1.5 mb-2">
                    <span className={`text-5xl font-black tracking-tight ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-sm pb-2 font-medium ${plan.highlight ? 'text-gray-400' : 'text-gray-500'}`}>/mo</span>
                  </div>
                  <p className={`text-sm ${plan.highlight ? 'text-gray-400' : 'text-gray-500'}`}>{plan.tagline}</p>
                </div>

                <ul className="space-y-3.5 mb-9 flex-1">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <CheckCircle size={14}
                                   className={`flex-shrink-0 mt-0.5 ${plan.highlight ? 'text-gray-400' : 'text-gray-400'}`} />
                      <span className={plan.highlight ? 'text-gray-200' : 'text-gray-700'}>{f}</span>
                    </li>
                  ))}
                </ul>

                <button onClick={() => navigate('/login')}
                        className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all hover:-translate-y-0.5 tracking-wide ${
                          plan.highlight
                            ? 'bg-white text-gray-900 hover:bg-gray-100 shadow-lg'
                            : 'bg-gray-900 text-white hover:bg-gray-700'
                        }`}>
                  Get started →
                </button>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-gray-500 mt-10">
            All prices exclude GST &nbsp;·&nbsp; 14-day free trial on all plans &nbsp;·&nbsp; No credit card required
          </p>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-t border-gray-100 py-28">
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">FAQ</p>
            <h2 className="text-4xl font-black text-gray-900 tracking-tight">Common questions.</h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((faq, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                        className="w-full flex items-center justify-between px-6 py-5 text-left font-semibold text-gray-900 hover:bg-gray-50 transition-colors">
                  <span className="pr-4">{faq.q}</span>
                  <ChevronDown size={15}
                               className={`text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 text-sm text-gray-500 leading-relaxed border-t border-gray-50">
                    <div className="pt-4">{faq.a}</div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────── */}
      <section className="bg-[#0F172A] py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-5 leading-tight">
            Your firm deserves better<br className="hidden sm:block" /> than a spreadsheet.
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto leading-relaxed">
            Join forward-thinking CA firms using DueDate to run a tighter, more profitable practice.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/login')}
                    className="flex items-center justify-center gap-2.5 bg-white text-gray-900 font-black px-8 py-4 rounded-xl hover:bg-gray-100 transition-all hover:-translate-y-0.5 shadow-2xl text-sm tracking-wide">
              Start your free trial <ArrowRight size={15} />
            </button>
            <a href="#pricing"
               className="flex items-center justify-center gap-2 bg-white/10 text-white font-semibold px-8 py-4 rounded-xl border border-white/15 hover:bg-white/15 transition-all text-sm">
              View pricing
            </a>
          </div>
          <p className="text-xs text-gray-400 mt-5 tracking-wide">
            14-day free trial &nbsp;·&nbsp; No credit card required
          </p>
        </div>
      </section>
      </main>

      {/* ── Footer ──────────────────────────────────────────────────── */}
      <footer className="bg-[#0F172A] border-t border-white/10 py-8">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 bg-white/10 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-xs">D</span>
            </div>
            <span className="font-bold text-white tracking-tight">DueDate</span>
          </div>
          <p className="text-xs text-gray-400">© {new Date().getFullYear()} DueDate. All rights reserved.</p>
          <button onClick={() => navigate('/login')}
                  className="text-xs font-semibold text-gray-400 hover:text-white transition-colors">
            Log in →
          </button>
        </div>
      </footer>

    </div>
  );
}
